import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AdminAvailabilityRequests, NotifyMeDemandSummary } from "@/components/sourcing/AvailabilityRequests";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, PackageOpen } from "lucide-react";
import { AdminSourcingActions } from "@/components/sourcing/AdminSourcingActions";
import { SourcingJourney } from "@/components/sourcing/SourcingLifecycle";

// RC49-SRC-014: only legal next steps are offered per state (mirrors services/sourcing_lifecycle.py)
const NEXT = {
  OPEN: ["EVALUATING", "UNABLE_TO_SOURCE"],
  EVALUATING: ["AVAILABILITY_CONFIRMED", "UNABLE_TO_SOURCE"],
  PRODUCTION_REQUIRED: ["AVAILABILITY_CONFIRMED", "UNABLE_TO_SOURCE"],
  QUOTE_REQUIRED: ["UNABLE_TO_SOURCE"],
  SOURCING_CONFIRMED: [],
  AVAILABILITY_CONFIRMED: ["UNABLE_TO_SOURCE"],
  OPTIONS_PRESENTED: ["UNABLE_TO_SOURCE"],
  PREFERENCE_SELECTED: ["CANCELLED_BEFORE_PRODUCTION"],
  FINAL_QUOTE: ["QUOTE_EXPIRED", "CANCELLED_BEFORE_PRODUCTION"],
  ACCEPTED_DEPOSIT_DUE: ["QUOTE_EXPIRED", "CANCELLED_BEFORE_PRODUCTION"],
  DEPOSIT_PAID_PRODUCTION_AUTHORIZED: ["IN_PRODUCTION", "CANCELLED_BEFORE_PRODUCTION"],
  IN_PRODUCTION: ["PRODUCTION_COMPLETE_BALANCE_DUE"],
  READY_TO_SHIP: ["IN_TRANSIT"],
  IN_TRANSIT: ["DELIVERED"],
};
const LABEL = {
  EVALUATING: "Start supply review", AVAILABILITY_CONFIRMED: "Confirm availability", IN_PRODUCTION: "Mark in production",
  PRODUCTION_COMPLETE_BALANCE_DUE: "Production complete — balance due", IN_TRANSIT: "Mark in transit", DELIVERED: "Mark delivered",
  UNABLE_TO_SOURCE: "Unable to source", QUOTE_EXPIRED: "Quote expired", CANCELLED_BEFORE_PRODUCTION: "Cancel before production",
};
const EXIT = new Set(["UNABLE_TO_SOURCE", "QUOTE_EXPIRED", "CANCELLED_BEFORE_PRODUCTION"]);
const STATUS_BADGE = {
  OPEN: "badge-reserved", EVALUATING: "badge-reserved", PRODUCTION_REQUIRED: "badge-committed",
  QUOTE_REQUIRED: "badge-committed", SOURCING_CONFIRMED: "badge-available", UNABLE_TO_SOURCE: "badge-sold",
  DEALER_DECLINED: "badge-sold", QUOTE_EXPIRED: "badge-sold", CANCELLED_BEFORE_PRODUCTION: "badge-sold", DELIVERED: "badge-available",
};
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

function Summary({ r }) {
  const q = r.final_quote;
  return (
    <div className="mt-2 flex flex-wrap gap-3 text-xs" data-testid={`admin-sourcing-summary-${r.id}`}>
      {r.preferred_path && <span className="rounded bg-alabaster px-2 py-1">Preference: <strong>{r.preferred_path.replaceAll("_", " ")}</strong></span>}
      {q && <span className="rounded bg-alabaster px-2 py-1">Quote rev {q.revision}: <strong>{money(q.total_usd)}</strong> · deposit {money(q.deposit_usd)} · balance {money(q.balance_usd)}</span>}
      {r.payments?.deposit && <span className="rounded bg-alabaster px-2 py-1">Deposit <strong>{money(r.payments.deposit.amount_usd)}</strong> recorded</span>}
      {r.payments?.final && <span className="rounded bg-alabaster px-2 py-1">Final <strong>{money(r.payments.final.amount_usd)}</strong> recorded</span>}
      <span className="rounded bg-alabaster px-2 py-1" data-testid={`admin-sourcing-shipto-${r.id}`}>
        Ship-to: {r.ship_to?.street ? <strong>{r.ship_to.recipient} · {r.ship_to.street}, {r.ship_to.city}, {r.ship_to.state} {r.ship_to.postal_code}</strong> : <span className="text-status-sold">not provided</span>}
      </span>
      {r.shipment && <span className="rounded bg-alabaster px-2 py-1">Shipment: <strong>{r.shipment.carrier}</strong>{r.shipment.tracking_number ? ` · ${r.shipment.tracking_number}` : ""}</span>}
    </div>
  );
}

export default function AdminSourcing() {
  const [rows, setRows] = useState(null);
  const [note, setNote] = useState({});
  const [timing, setTiming] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sourcing-requests");
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (r, status) => {
    setBusy(r.id);
    try {
      await api.post(`/admin/sourcing-requests/${r.id}/status`, { status, note: note[r.id] || "", timing_note: timing[r.id] || null });
      toast.success(`Marked: ${LABEL[status] || status} — dealer notified`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!rows) {
    return <div className="flex min-h-[40vh] items-center justify-center" data-testid="admin-sourcing-loading"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;
  }

  return (
    <div data-testid="admin-sourcing-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Operations</p>
      <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Sourcing queue</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
        Dealer sourcing requests with live evaluation context: what Florida can fulfill now, what is incoming, and
        what exists at origin. Only record timing you have verified — the dealer sees exactly what you confirm.
      </p>

      {rows.length === 0 ? (
        <div className="mt-8 rounded-md border border-dashed border-limestone bg-white p-10 text-center" data-testid="admin-sourcing-empty">
          <PackageOpen className="mx-auto h-8 w-8 text-warmgrey" />
          <p className="mt-3 text-sm text-warmgrey">No sourcing requests.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border border-border bg-white p-4" data-testid={`admin-sourcing-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="font-mono text-xs font-semibold">{r.sku}</span>
                  <span className="ml-2 text-sm text-charcoal">{r.color_name}{r.us_color_name ? ` · ${r.us_color_name}` : ""} · {r.format_in}</span>
                  <p className="mt-0.5 text-xs text-warmgrey">
                    {r.dealer_name} · {r.requested_by} · {r.boxes} boxes ({r.sqft} sq ft)
                    {r.requested_delivery_date ? ` · wants ${r.requested_delivery_date}` : ""}
                    {r.project_id ? " · linked project" : ""} · {new Date(r.created_at).toLocaleString()}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs" data-testid={`sourcing-evaluation-${r.id}`}>
                    <span className="rounded bg-sand px-2 py-1">Florida available: <strong>{r.evaluation.florida_atp_boxes}</strong> boxes</span>
                    <span className="rounded bg-sand px-2 py-1">Incoming: <strong>{r.evaluation.incoming_boxes}</strong> boxes</span>
                    <span className="rounded bg-sand px-2 py-1">At origin: <strong>{r.evaluation.origin_boxes}</strong> boxes</span>
                    {r.evaluation.pre_reservable_boxes > 0 && <span className="rounded bg-sand px-2 py-1">Pre-reservable: <strong>{r.evaluation.pre_reservable_boxes}</strong></span>}
                  </div>
                  <Summary r={r} />
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase ${STATUS_BADGE[r.status] || "badge-reserved"}`}
                  data-testid={`admin-sourcing-status-${r.id}`}>{r.status_label}</span>
              </div>
              {r.journey && <SourcingJourney journey={r.journey} id={`admin-${r.id}`} />}
              {(NEXT[r.status] || []).length > 0 && (
                <div className="mt-3 border-t border-limestone pt-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input value={note[r.id] || ""} onChange={(e) => setNote({ ...note, [r.id]: e.target.value })}
                      placeholder="Note to dealer (optional)" className="h-9 bg-white" data-testid={`sourcing-note-${r.id}`} />
                    <Input value={timing[r.id] || ""} onChange={(e) => setTiming({ ...timing, [r.id]: e.target.value })}
                      placeholder="Verified timing (optional)" className="h-9 bg-white" data-testid={`sourcing-timing-input-${r.id}`} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(NEXT[r.status] || []).map((k) => (
                      <Button key={k} size="sm" variant="outline" onClick={() => setStatus(r, k)} disabled={!!busy}
                        className={EXIT.has(k) ? "border-status-sold text-status-sold" : "border-limestone"}
                        data-testid={`sourcing-set-${r.id}-${k.toLowerCase()}`}>
                        {busy === r.id && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}{LABEL[k] || k}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              <AdminSourcingActions r={r} reload={load} />
              {r.ship_to?.freight_reconfirmation_required && <p className="mt-2 text-xs text-status-sold" data-testid={`sourcing-reconfirm-flag-${r.id}`}>Shipping quote requires reconfirmation (address changed after quote)</p>}
            </div>
          ))}
        </div>
      )}
      <NotifyMeDemandSummary />
      <AdminAvailabilityRequests />
    </div>
  );
}
